<script setup lang="ts">
import { EditableArea } from "@magnolia/vue-editor";
import { Heading } from "~/components/clarity-ui/Heading";
import { DashboardItem } from "~/components/MyBrita/Dashboard/DashboardItem";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import AccountGrid from "~/templates/components/account/AccountGrid.vue";
import RichTextComponent from "~/templates/components/cms/RichTextComponent.vue";
import AccountAddressCard from "~/templates/components/shop/account/AccountAddressCard.vue";
import type { mgnlHeadline } from "~/types/magnolia";

export interface AccountAddressBookProps {
  subscriptionArea?: {
    buttonLabel?: string;
    headline?: mgnlHeadline;
    richText?: string;
  };
}

defineProps<AccountAddressBookProps>();

defineOptions({
  inheritAttrs: false,
});

const loadingData = ref(true);
const { loadCustomerAddresses } = useAddress();

const { userDefaultBillingAddress, userDefaultShippingAddress } = useUser();
const { marketSetupLinks } = useMarketSetup();

useBreadcrumbs([
  {
    name: "Account Overview",
    path: "/account",
  },
  {
    name: "Address",
    path: "/account/address",
  },
]);

onBeforeMount(async () => {
  await loadCustomerAddresses();
  loadingData.value = false;
});
</script>

<template>
  <AccountGrid>
    <div class="account-address-book grid gap-4 xl:grid-cols-2 xl:gap-6">
      <div class="col-span-full mb-4">
        <Heading color="blue" tag="h1">
          <CommonLabel path="BWF/shop/account/addressBook/addressBookHeadline" />
        </Heading>
      </div>

      <DashboardItem>
        <AccountAddressCard :address="userDefaultBillingAddress" is-billing>
          <template #editButton>
            <button class="btn btn-blue w-full xl:mt-auto xl:self-start">
              <CommonLabel path="BWF/shop/shared/edit" />
            </button>
          </template>
        </AccountAddressCard>
      </DashboardItem>

      <DashboardItem>
        <AccountAddressCard :address="userDefaultShippingAddress" is-shipping>
          <template #editButton>
            <button class="btn btn-blue w-full xl:mt-auto xl:self-start">
              <CommonLabel path="BWF/shop/shared/edit" />
            </button>
          </template>
        </AccountAddressCard>
      </DashboardItem>

      <DashboardItem class="xl:col-span-full">
        <EditableArea :content="subscriptionArea">
          <div class="flex flex-col gap-4">
            <Heading v-if="subscriptionArea.headline" size="small" tag="h2">
              {{ subscriptionArea.headline.text }}
            </Heading>
            <RichTextComponent
              v-if="subscriptionArea.richText"
              class="mb-2"
              :rich-text="subscriptionArea.richText"
            />
            <NuxtLink
              v-if="marketSetupLinks.myBritaSubscriptionPage"
              :to="marketSetupLinks.myBritaSubscriptionPage"
              class="btn btn-ghost self-start xl:mt-auto"
              >{{ subscriptionArea.buttonLabel }}</NuxtLink
            >
          </div>
        </EditableArea>
      </DashboardItem>
    </div>
  </AccountGrid>
</template>
